/* ============================================================
   DYC Toast Notification System
   Glass morphism toasts with auto-dismiss and progress bar
   Public API: window.DYCToast.show(), .dismiss(), .dismissAll()
   ============================================================ */
(function () {
  'use strict';

  if (window.__DYCToastInitialized) return;
  window.__DYCToastInitialized = true;

  /* ---- Constants ---- */
  var MAX_VISIBLE = 2;
  var ENTER_DURATION = 300;
  var EXIT_DURATION = 200;

  /* Default config per toast type */
  var TYPE_DEFAULTS = {
    success:  { icon: 'circle-check',  duration: 4000 },
    heart:    { icon: 'heart',         duration: 4000 },
    shipping: { icon: 'truck',         duration: 5000 },
    cart:     { icon: 'package-check', duration: 3000 },
    warning:  { icon: 'package',       duration: 5000 }
  };

  /* ---- State ---- */
  var toastCounter = 0;
  var timers = {};       /* toastId → setTimeout handle */
  var remaining = {};    /* toastId → ms remaining when paused */
  var pausedAt = {};     /* toastId → timestamp when paused */
  var durations = {};    /* toastId → original duration */

  /* ---- Helpers ---- */

  /**
   * Get or lazily validate the toast container element.
   */
  function getContainer() {
    var el = document.getElementById('dyc-toast-container');
    if (!el) {
      /* Safety fallback — create container if missing */
      el = document.createElement('div');
      el.id = 'dyc-toast-container';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    return el;
  }

  /**
   * Create a Lucide icon element.
   * Uses data-lucide attribute + createIcons() since the UMD build
   * doesn't expose createElement(). Falls back to inline SVG paths.
   */
  var ICON_SVGS = {
    'circle-check': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
    'heart': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>',
    'truck': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M5 18H3c-.6 0-1-.4-1-1V7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v11"/><path d="M14 9h4l4 4v4c0 .6-.4 1-1 1h-2"/><circle cx="7" cy="18" r="2"/><path d="M15 18H9"/><circle cx="17" cy="18" r="2"/></svg>',
    'package-check': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m16 16 2 2 4-4"/><path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/><path d="m7.5 4.27 9 5.15"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>',
    'package': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/></svg>',
    'alert-circle': '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
  };

  function createIcon(name) {
    var container = document.createElement('div');
    container.innerHTML = ICON_SVGS[name] || ICON_SVGS['circle-check'];
    return container.firstElementChild;
  }

  /**
   * Enforce maximum visible toasts — remove oldest if over limit.
   */
  function enforceMax(container) {
    while (container.children.length > MAX_VISIBLE) {
      var oldest = container.firstElementChild;
      if (oldest) {
        var id = parseInt(oldest.getAttribute('data-toast-id'), 10);
        removeToast(oldest, id);
      }
    }
  }

  /**
   * Immediately remove a toast element and clean up its timer.
   */
  function removeToast(el, id) {
    clearTimeout(timers[id]);
    delete timers[id];
    delete remaining[id];
    delete pausedAt[id];
    delete durations[id];
    if (el && el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  /* ---- Core API ---- */

  /**
   * Show a toast notification.
   *
   * @param {Object} opts
   * @param {string}   opts.type       - 'success'|'heart'|'shipping'|'cart'|'warning'
   * @param {string}   opts.title      - Bold title text
   * @param {string}   [opts.desc]     - Description text
   * @param {Function} [opts.action]   - Callback for action button
   * @param {string}   [opts.actionText] - Button label (if 'Undo', renders as link)
   * @param {number}   [opts.duration] - Auto-dismiss ms (overrides type default)
   * @param {string}   [opts.icon]     - Lucide icon name (overrides type default)
   * @param {number}   [opts.progress] - 0-100 mini progress bar (warning type)
   * @returns {number} toastId
   */
  function show(opts) {
    opts = opts || {};
    var type = opts.type || 'success';
    var defaults = TYPE_DEFAULTS[type] || TYPE_DEFAULTS.success;
    var icon = opts.icon || defaults.icon;
    var duration = opts.duration || defaults.duration;
    var id = ++toastCounter;

    var container = getContainer();

    /* ---- Build DOM ---- */
    var toast = document.createElement('div');
    toast.className = 'dyc-toast dyc-toast--' + type + ' dyc-toast--entering';
    toast.setAttribute('data-toast-id', id);

    /* Inner wrapper */
    var inner = document.createElement('div');
    inner.className = 'dyc-toast-inner';

    /* Icon */
    var iconWrap = document.createElement('div');
    iconWrap.className = 'dyc-toast-icon';
    iconWrap.appendChild(createIcon(icon));
    inner.appendChild(iconWrap);

    /* Text */
    var textWrap = document.createElement('div');
    textWrap.className = 'dyc-toast-text';

    if (opts.title) {
      var titleEl = document.createElement('div');
      titleEl.className = 'dyc-toast-title';
      titleEl.textContent = opts.title;
      textWrap.appendChild(titleEl);
    }

    if (opts.desc) {
      var descEl = document.createElement('div');
      descEl.className = 'dyc-toast-desc';
      descEl.textContent = opts.desc;
      textWrap.appendChild(descEl);
    }

    /* Mini progress bar (warning + progress) */
    if (type === 'warning' && typeof opts.progress === 'number') {
      var miniTrack = document.createElement('div');
      miniTrack.className = 'dyc-toast-mini-progress';
      var miniFill = document.createElement('div');
      miniFill.className = 'dyc-toast-mini-fill';
      miniFill.style.width = Math.max(0, Math.min(100, opts.progress)) + '%';
      miniTrack.appendChild(miniFill);
      textWrap.appendChild(miniTrack);
    }

    inner.appendChild(textWrap);

    /* Action button or Undo link */
    if (opts.action && opts.actionText) {
      var btn = document.createElement('button');
      btn.type = 'button';

      if (opts.actionText === 'Undo') {
        btn.className = 'dyc-toast-undo';
      } else {
        btn.className = 'dyc-toast-action';
      }

      btn.textContent = opts.actionText;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        opts.action();
        dismiss(id);
      });
      inner.appendChild(btn);
    }

    toast.appendChild(inner);

    /* Progress bar */
    var progressWrap = document.createElement('div');
    progressWrap.className = 'dyc-toast-progress';
    var progressBar = document.createElement('div');
    progressBar.className = 'dyc-toast-progress-bar';
    progressBar.style.animationDuration = duration + 'ms';
    progressWrap.appendChild(progressBar);
    toast.appendChild(progressWrap);

    /* ---- Append & enforce max ---- */
    container.appendChild(toast);
    enforceMax(container);

    /* ---- Remove entering class after animation ---- */
    setTimeout(function () {
      toast.classList.remove('dyc-toast--entering');
    }, ENTER_DURATION);

    /* ---- Auto-dismiss timer ---- */
    durations[id] = duration;
    remaining[id] = duration;
    timers[id] = setTimeout(function () {
      dismiss(id);
    }, duration);

    /* ---- Hover pause / resume ---- */
    toast.addEventListener('mouseenter', function () {
      /* Calculate remaining time */
      if (timers[id]) {
        clearTimeout(timers[id]);
        delete timers[id];
      }
      pausedAt[id] = Date.now();

      /* Pause CSS animation */
      var bar = toast.querySelector('.dyc-toast-progress-bar');
      if (bar) {
        bar.style.animationPlayState = 'paused';
      }
    });

    toast.addEventListener('mouseleave', function () {
      /* Calculate how much time was left */
      if (pausedAt[id] && remaining[id]) {
        var elapsed = Date.now() - pausedAt[id];
        /* remaining was set on last pause or initial show */
        /* We need to compute: remaining[id] was set when timer was created,
           but actual elapsed since timer start = duration - remaining + elapsed.
           Simpler: remaining stays whatever it was minus nothing (we paused),
           so just restart with remaining[id] minus 0 (time paused doesn't count). */
      }
      delete pausedAt[id];

      /* Resume CSS animation */
      var bar = toast.querySelector('.dyc-toast-progress-bar');
      if (bar) {
        bar.style.animationPlayState = 'running';
      }

      /* Restart timer for remaining duration */
      /* On first hover, remaining = original. Subsequent hovers preserve remaining. */
      var timeLeft = remaining[id];
      if (typeof timeLeft === 'number' && timeLeft > 0) {
        timers[id] = setTimeout(function () {
          dismiss(id);
        }, timeLeft);
      }
    });

    /* Track elapsed time for accurate remaining calculation */
    var startTime = Date.now();

    /* Override mouseenter to compute accurate remaining */
    toast.addEventListener('mouseenter', function () {
      if (timers[id]) {
        var now = Date.now();
        var elapsed = now - startTime;
        remaining[id] = Math.max(0, durations[id] - elapsed);
      }
    }, true); /* capture phase — runs before the other mouseenter */

    return id;
  }

  /**
   * Dismiss a toast by ID with exit animation.
   *
   * @param {number} toastId
   */
  function dismiss(toastId) {
    var toast = document.querySelector('[data-toast-id="' + toastId + '"]');
    if (!toast) return;

    /* Prevent double-dismiss */
    if (toast.classList.contains('dyc-toast--exiting')) return;

    /* Clear auto-dismiss timer */
    clearTimeout(timers[toastId]);
    delete timers[toastId];

    /* Exit animation */
    toast.classList.add('dyc-toast--exiting');

    setTimeout(function () {
      removeToast(toast, toastId);
    }, EXIT_DURATION);
  }

  /**
   * Dismiss all visible toasts.
   */
  function dismissAll() {
    var container = document.getElementById('dyc-toast-container');
    if (!container) return;

    var toasts = container.querySelectorAll('.dyc-toast');
    for (var i = 0; i < toasts.length; i++) {
      var id = parseInt(toasts[i].getAttribute('data-toast-id'), 10);
      dismiss(id);
    }
  }

  /* ---- Public API ---- */
  window.DYCToast = {
    show: show,
    dismiss: dismiss,
    dismissAll: dismissAll
  };

})();
